"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, CalendarCheck, ShieldCheck, Clock, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const trustHighlights = [{
  id: 1,
  label: "24/7 Fast Local Dispatch in Jonesboro"
}, {
  id: 2,
  label: "Clear, Upfront Pricing Guarantee"
}, {
  id: 3,
  label: "Direct Phone Support — No Call Centers"
}];
export default function PlumberNow() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut",
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };
  return <section id="need-a-plumber-now" data-nav="light" className="relative bg-background py-14 md:py-20 lg:py-24 overflow-hidden border-t border-border">
      <div className="container mx-auto max-w-[1240px] px-5 md:px-10">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.25
      }} className="relative rounded-3xl bg-primary text-primary-foreground p-8 md:p-14 lg:p-16 shadow-2xl overflow-hidden border border-border/20">
          {/* Subtle decorative background glow and geometric line */}
          <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-accent/15 blur-3xl" />
          <div aria-hidden="true" className="pointer-events-none absolute -left-20 -bottom-20 h-80 w-80 rounded-full bg-chart-3/15 blur-3xl" />

          <div className="relative z-10 max-w-3xl mx-auto text-center flex flex-col items-center">
            {/* Urgency Status Eyebrow */}
            <motion.div variants={itemVariants} className="inline-flex items-center gap-2 rounded-full bg-accent/20 border border-accent/40 px-3.5 py-1.5 text-xs md:text-sm font-bold uppercase tracking-wider text-accent mb-6">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-accent" />
              </span>
              <span className="font-sans">Emergency Dispatch Active</span>
              <span className="text-primary-foreground/60 hidden sm:inline">|</span>
              <span className="text-primary-foreground/90 font-medium hidden sm:inline">Jonesboro, GA</span>
            </motion.div>

            {/* Main Urgent Headline */}
            <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-primary-foreground leading-tight md:leading-tight mb-5">
              Need an Emergency Plumber Right Now?
            </motion.h2>

            {/* Body Copy */}
            <motion.p variants={itemVariants} className="text-base md:text-lg text-primary-foreground/85 max-w-2xl leading-relaxed mb-8 md:mb-10 font-sans">
              Burst pipe, overflowing drain, or no hot water? Do not let water damage destroy your home.
              Our local Jonesboro plumbers are on call 24/7 with immediate response and upfront pricing.
            </motion.p>

            {/* Signature Dual-Action CTAs */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4 w-full sm:w-auto mb-10">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 hover:brightness-105 active:scale-98 rounded-full font-bold px-8 py-6 text-base md:text-lg shadow-lg transition-[transform,filter,box-shadow] duration-150 group cursor-pointer">
                <a href="tel:4708479076" className="flex items-center justify-center gap-3" aria-label="Call emergency plumber at (470) 847-9076">
                  <PhoneCall className="size-5 shrink-0 transition-transform duration-150 group-hover:scale-110" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>

              <Button asChild size="lg" variant="outline" className="bg-background text-foreground hover:bg-secondary hover:text-secondary-foreground active:scale-98 rounded-full font-semibold px-8 py-6 text-base md:text-lg border-0 shadow-md transition-[transform,background-color,color] duration-150 group cursor-pointer">
                <Link to="/contact/" className="flex items-center justify-center gap-2">
                  <CalendarCheck className="size-5 shrink-0 text-primary" />
                  <span>Request Service</span>
                  <ArrowRight className="size-4 shrink-0 transition-transform duration-150 group-hover:translate-x-1" />
                </Link>
              </Button>
            </motion.div>

            {/* Verification Proof Badges */}
            <motion.div variants={itemVariants} className="w-full pt-8 border-t border-primary-foreground/15 grid grid-cols-1 sm:grid-cols-3 gap-4 text-left sm:text-center">
              {trustHighlights.map((highlight, index) => <div key={highlight.id} data-index={index} className="flex items-center justify-start sm:justify-center gap-2.5 text-xs md:text-sm font-medium text-primary-foreground/90">
                  <CheckCircle2 className="size-4 shrink-0 text-accent" />
                  <span>{highlight.label}</span>
                </div>)}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}